import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getBillTo, getShipTo } from "@insite/client-framework/Services/CustomersService";
import { Session } from "@insite/client-framework/Services/SessionService";
import { CartLineModel, CartModel, ProductDto, QuoteLineModel } from "@insite/client-framework/Types/ApiModels";

declare global {
    interface Window {
        protonAddToCart: any;
    }
}

export interface GA4ProductListItem {
    event: string;
    add_to_cart_type?: string;
    list_id?: string;
    list_name?: string;
    ecommerce: {
        items: KeyPair<string, string | number>[];
    };
}
export interface GA4Purchase {
    event: string;
    ecommerce: {
        items: KeyPair<string, string | number>[];
        currency: string;
        value: number;
        tax: number;
        shipping: number;
        affiliation: string;
        transaction_id: string;
        coupon?: string;
    };
}

export interface ProtonAIProductListItem {
    event: string;
    ecommerce: {
        items: [
            {
                item_id: string;
                item_name: string;
                price: number;
                item_brand: string;
                quantity: number;
                item_variant: string;
            },
        ];
    };
}

export interface ProtonAISession {
    event: string;
    is_logged_in: string;
    bill_to_id: string;
    ship_to_id: string;
    user_email: string;
}

type KeyPair<K extends string, V> = {
    [key in K]: V;
};

// OOTB from file /modules/client-framework/src/Common/Utilities/tracking.ts
// get global for google tag manager
function getDataLayer() {
    return (window as any).dataLayer;
}

function setData(eventData: any) {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        if (eventData.ecommerce) {
            dataLayer.push({ ecommerce: null });
        }
        dataLayer.push(eventData);
    } else {
        (window as any).dataLayer = [eventData];
    }
}

// cartlines is either a QuoteLineModel[], a CartLineModel[] or a ProductModel (or keep adding more types)
export const setGA4Event = (
    cartLines: QuoteLineModel[] | CartLineModel[] | CartModel | ProductDto[] | [],
    eventName: string,
    additionalInfo?: any | null,
) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    const eventData = {
        event: eventName || "",
        ecommerce: {
            items: [],
        },
    } as GA4ProductListItem;

    if (eventName === "request_quote") {
        const items = cartLines as QuoteLineModel[];
        items.forEach(item => {
            const itemData = {
                item_name: item.shortDescription ?? "",
                item_id: item.productId ?? "",
                price: item.pricing?.actualPrice ?? 0,
                item_brand: item.brand?.name ?? "",
                item_category: "", // is sent empty for the moment
                item_category2: "", // is sent empty for the moment
                quantity: item.qtyOrdered ?? 0,
                item_variant: "", // is sent empty for the moment
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_to_cart") {
        const items = cartLines as CartLineModel[];
        eventData.add_to_cart_type = items.some(item => item && item.quoteRequired) ? "Request Quote" : "Add to Cart";
        items.forEach((item, index) => {
            if (!item) {
                return;
            }

            const itemData = {
                item_name: item.shortDescription ?? "",
                item_id: item.productId ?? item.id ?? "",
                price: item.pricing?.actualPrice ?? 0,
                item_brand: item.brand?.name ?? "",
                item_category: "", // is sent empty for the moment
                item_category2: "", // is sent empty for the moment
                index: additionalInfo?.index !== -1 && additionalInfo?.index ? additionalInfo?.index : index + 1,
                quantity: item.qtyOrdered ?? 0,
                ...(additionalInfo?.item_list_id && {item_list_id: additionalInfo?.item_list_id ?? ""}),
                ...(additionalInfo?.item_list_name && {item_list_name: additionalInfo?.item_list_name ?? ""}),
                item_variant: item.erpNumber ?? "",
            } as KeyPair<string, string | number>;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_to_wishlist") {
        const items = cartLines as any[];
        items.forEach(item => {
            const itemData = {
                item_name: item.productTitle ?? "",
                item_id: item.id ?? "",
                price: item.actualPrice ?? 0,
                item_brand: item.brand?.name ?? "",
                item_category: "", // is sent empty for the moment
                item_category2: "", // is sent empty for the moment
                quantity: item.qtyOrdered ?? 0,
            } as KeyPair<string, string | number>;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "purchase") {
        const cart = cartLines as CartModel;
        const eventData = {
            event: eventName || "",
            ecommerce: {
                currency: "USD",
                value: cart.orderGrandTotal || 0,
                tax: cart.totalTax || 0,
                shipping: cart.shippingAndHandling || 0,
                affiliation: "",
                transaction_id: cart.orderNumber || cart.erpOrderNumber || "",
                coupon: cart.promotionCode || "",
                items: [],
            },
        } as unknown as GA4Purchase;

        cart.cartLines!.forEach(item => {
            if (item.quoteRequired === false) {
                const itemData = {
                    item_name: item.shortDescription ?? "",
                    item_id: item.productId ?? "",
                    price: item.pricing?.actualPrice ?? 0,
                    item_brand: item.brand?.name ?? "",
                    item_category: "", // is sent empty for the moment
                    item_category2: "", // is sent empty for the moment
                    item_variant: item.erpNumber ?? "",
                    quantity: item.qtyOrdered ?? 0,
                    item_coupon: "", // is sent empty for the moment
                } as KeyPair<string, string | number>;
                eventData.ecommerce.items.push(itemData);
            }
        });

        setData(eventData);

        return;
    }

    if (eventName === "view_cart") {
        const items = cartLines as any[];

        items.forEach(item => {
            const itemData = {
                item_erp_number: item.erpNumber ?? "",
                item_name: item.shortDescription ?? "",
                item_id: item.productId ?? "",
                price: item.pricing?.actualPrice ?? 0,
                item_brand: item.brand?.name ?? "",
                item_category: "", // is sent empty for the moment
                item_category2: "", // is sent empty for the moment
                item_variant: "", // is sent empty for the moment
                quantity: item.qtyOrdered ?? 0,
            } as KeyPair<string, string | number>;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "sign_in" || eventName === "session") {
        const { userID, sapAccountNumber } = additionalInfo;
        const eventData = {
            user_id: userID,
            sap_account_number: sapAccountNumber,
        } as KeyPair<string, string>;

        if (eventName === "sign_in") {
            eventData.event = "login";
        }

        setData(eventData);

        return;
    }

    if (eventName === "file_download" || eventName === "sap_file_download") {
        const eventData = {
            event: eventName || "",
            file_name: additionalInfo.file_name,
            file_extension: additionalInfo.file_extension,
            link_text: additionalInfo.link_text,
            link_url: additionalInfo.link_url,
            pageTitle: document.title,
        } as KeyPair<string, string>;
        setData(eventData);

        return;
    }

    if (eventName === "buy_again" || eventName === "reorder") {
        const { sapAccountNumber } = additionalInfo;
        const eventData = {
            event: eventName || "",
            sap_account_number: sapAccountNumber,
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if (
        eventName === "remove_item" ||
        eventName === "share_list" ||
        eventName === "create_list"
    ) {
        const eventData = {
            event: eventName || "",
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if(eventName === "search_bar_navigations") {
        const { searchTerm, product, category } = additionalInfo;
        const eventData = {
            event: eventName || "",
            search_term: searchTerm,
            products: product ? product.title : "",
            categories: category ? category.title : "",
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if(eventName === "createAccountSubmission") {
        const { customertype, formType, formID,accountNumber,industrySelection,businessType } = additionalInfo;
        const eventData = {
            event: eventName || "",
            customer_type: customertype,
            formType,
            formID,
            sap_account_number: accountNumber,
            industry_selection:industrySelection,
            business_type:businessType,
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if(eventName === "formSubmissionSuccess") {
        const { formType, formID,accountNumber } = additionalInfo;
        const eventData = {
            event: eventName || "",
            formType,
            formID,
            sap_account_number: accountNumber !== "" ? accountNumber : "54080",
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if (
        eventName === "view_order" || 
        eventName === "download_order" || 
        eventName === "change_order"
    ) {
        const { status, orderType, value } = additionalInfo;
        const eventData = {
            event: eventName,
            order_status: status,
            order_type: orderType,
            value,
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if (
        eventName === "view_quote" ||
        eventName === "quote_place_order" ||
        eventName === "edit_quote" ||
        eventName === "requote_quote" ||
        eventName === "cancel_quote"
    ) {
        const { status, orderType, value, quantity } = additionalInfo;
        let customEventName = eventName;
        if (eventName === "quote_place_order") {
            customEventName = "place_order";
        }
        const eventData = {
            event: customEventName,
            quote_status: status,
            order_type: orderType,
            value,
            quantity,
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    if (
        eventName === "submit_quote_request"
    ) {
        const { billtoNumber } = additionalInfo;
        const items = cartLines as CartLineModel[];
        const brands = items?.map(item => item?.brand?.name) || [];
        const erpNumbers = items?.map(item => item.erpNumber) || [];

        const eventData = {
            event: eventName,
            sap_account_number: billtoNumber || "",
            item_brand: brands,
            item_erp_number: erpNumbers,
            quote_quantity: items.length,
        } as KeyPair<string, string | number | any>;

        setData(eventData); 

        return;
    }

    if(eventName === "return_request_order") {
        const { returnOrderReason, itemsToReturn } = additionalInfo;
        const eventData = {
            event: eventName,
            return_order_reason:returnOrderReason,
            items_to_return:itemsToReturn,
        } as KeyPair<string, string>;

        setData(eventData);

        return;
    }

    setData(eventData);
};

export const addProtonDataLayerPDP = (eventName: string, gaProductInfos: {
    productNumber?: string,
    brandName?: string,
    productTitle?: string,
    productId?: string,
    actualPrice?: number,
    qtyOrdered?: number
}) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    const { productNumber, brandName, productTitle, productId, actualPrice, qtyOrdered } = gaProductInfos;

    const eventData = {
        event: eventName || "",
        ecommerce: {
            items: [
                {
                    item_id: productNumber || "",
                    item_name: productTitle || "",
                    price: actualPrice || "",
                    item_brand: brandName || "",
                    quantity: qtyOrdered || "",
                    item_variant: productNumber || ""
                },
            ],
        },
    } as ProtonAIProductListItem;

    setData(eventData);
};

export const addProtonDataLayerSession = async (eventName: string, session: Session) => {
    if (IS_SERVER_SIDE || !session) {
        return;
    }

    const billTo = session?.isAuthenticated
        ? await getBillTo({
              billToId: API_URL_CURRENT_FRAGMENT,
          })
        : null;

    const shipTo = session?.isAuthenticated
        ? await getShipTo({
              billToId: API_URL_CURRENT_FRAGMENT,
              shipToId: session?.shipToId ?? "",
          })
        : null;

    const eventData = {
        event: eventName || "",
        is_logged_in: session.isAuthenticated ? "true" : "false",
        bill_to_id: session.isAuthenticated ? billTo?.customerNumber.replace(/^0+/, "") : "",
        ship_to_id: session.isAuthenticated ? shipTo?.customerSequence.replace(/^0+/, "") : "",
        user_email: session.email ?? "",
    } as ProtonAISession;

    setData(eventData);
};

/**
 * Add to cart function for proton
 * @protected
 * eg: window.protonAddToCart('c3c5b1e0-684f-4d17-ac4d-ac2b01771387', 1, 'FT')
 * eg: window.protonAddToCart('c114ab3a-72a5-4016-bae9-ac2b017710c9', 500, 'FT', '','', 'true')
 */

export const addProtonAddToCart = (
    addToCartCustom: any,
    openAddedToCartConfirmation: (cartline: CartLineModel) => void,
) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    window.protonAddToCart = async (
        productId: string,
        qtyOrdered: number,
        unitOfMeasure: string,
        looseEnds: string | null = null,
        isCustNCNR?: string | null,
        isQuotedByUser?: string | null,
    ) => {
        /* Verndale Custom Feature - Define properties object to be added to cartline */
        const properties: { [key: string]: string } = {};

        if (looseEnds && looseEnds !== "") {
            properties.looseEnds = looseEnds;
        }

        if (isCustNCNR && isCustNCNR !== "") {
            properties.isCustNCNR = isCustNCNR;
        }

        if (isQuotedByUser && isQuotedByUser !== "") {
            properties.isQuotedByUser = isQuotedByUser;
        }

        const cartline = (await addToCartCustom({
            productId,
            qtyOrdered,
            unitOfMeasure,
            properties,
        } as any)) as CartLineModel;

        if (cartline) {
            openAddedToCartConfirmation(cartline);
        }
    };
};

export const setGA4SapNumberEvent = (sapNumber: string) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    setData({
        sapNumber,
    });
};

export const setGA4CustomEvent = (customEvent: any) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    setData(customEvent);
};
